import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth as firebaseAuth, functions } from '@config/firebase';
import { useAuth, useToast, useLanguage } from '@contexts';
import { Modal } from '@components/common';
import { BrandPanel } from './BrandPanel';

/**
 * Pantalla unificada de login + registro.
 * Diseño split-screen con transición "Slats" (cortina veneciana dorada).
 *
 * Layout:
 *   - Pane izquierdo (siempre visible): BrandPanel con eslogan que cross-fade
 *   - Pane derecho: dos formularios (login y register) superpuestos, solo uno
 *     visible según `mode`. Al cambiar, 9 lamas doradas barren la zona.
 *
 * Toda la lógica está conectada a Firebase Auth y Cloud Functions reales:
 *   - loginWithEmail / loginWithGoogle del AuthContext
 *   - signUpFreeOrg (Cloud Function callable)
 *   - sendPasswordResetEmail (Cloud Function callable)
 */
export const AuthScreen = ({ initialMode = 'login' }) => {
  const { loginWithEmail, loginWithGoogle } = useAuth();
  const { addToast } = useToast();
  const { t } = useLanguage();

  const [mode, setMode] = useState(initialMode);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => { setMode(initialMode); }, [initialMode]);

  // Cambio interno entre login y register: NO toca la URL para no provocar
  // remount del componente. La animación Slats se re-dispara incrementando
  // animKey, que se pasa como key={animKey} al div .shatter-slats.
  const switchTo = (m) => {
    if (m === mode) return;
    setMode(m);
    setAnimKey(k => k + 1);
  };

  // ============ ESTADO LOGIN ============
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      addToast(t('fillAllFields'), 'error');
      return;
    }
    setLoginLoading(true);
    try {
      await loginWithEmail(email, password);
      addToast(t('sessionStarted'), 'success');
    } catch (error) {
      console.error('Login error:', error);
      if (error.message === 'invalid-credentials') addToast('Email o contraseña incorrectos', 'error');
      else addToast(t('loginError'), 'error');
    }
    setLoginLoading(false);
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    try {
      await loginWithGoogle();
      addToast(t('sessionStarted'), 'success');
    } catch (error) {
      if (error.message === 'user-rejected') addToast('No se pudo completar el acceso con Google. Tu cuenta no tiene acceso a TorneosPro.', 'error');
      else if (error.code !== 'auth/popup-closed-by-user') addToast(t('loginError'), 'error');
    }
    setLoginLoading(false);
  };

  // ============ ESTADO REGISTRO ============
  const [regForm, setRegForm] = useState({
    name: '', email: '', password: '', organizationName: '', city: '', accept: false,
  });
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(null);
  const updateReg = (k, v) => setRegForm(f => ({ ...f, [k]: v }));

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regForm.name || !regForm.email || !regForm.password || !regForm.organizationName) {
      addToast('Completa todos los campos obligatorios', 'error');
      return;
    }
    if (regForm.password.length < 6) {
      addToast('La contraseña debe tener al menos 6 caracteres', 'error');
      return;
    }
    if (!regForm.accept) {
      addToast('Debes aceptar los términos y condiciones', 'error');
      return;
    }
    setRegLoading(true);
    try {
      const fn = httpsCallable(functions, 'signUpFreeOrg');
      await fn({
        email: regForm.email.toLowerCase().trim(),
        password: regForm.password,
        name: regForm.name.trim(),
        organizationName: regForm.organizationName.trim(),
        city: regForm.city.trim(),
      });
      setRegSuccess(regForm.email);
    } catch (err) {
      console.error('Register error:', err);
      const msg = err.message || 'Error al crear cuenta';
      addToast(msg.replace('FirebaseError: ', ''), 'error');
    }
    setRegLoading(false);
  };

  const handleResendVerify = async () => {
    if (!regSuccess) return;
    try {
      const fn = httpsCallable(functions, 'resendVerificationEmail');
      await fn({ email: regSuccess });
      addToast('Email de verificación reenviado', 'success');
    } catch {
      addToast('No se pudo reenviar', 'error');
    }
  };

  // ============ RESET PASSWORD ============
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      addToast('Introduce un email válido', 'error');
      return;
    }
    setResetLoading(true);
    try {
      const fn = httpsCallable(functions, 'sendPasswordResetEmail');
      await fn({ email: resetEmail.toLowerCase().trim() });
      setResetSent(true);
    } catch (err) {
      if (err.code === 'functions/not-found') addToast('Ese email no está registrado en TorneosPro', 'error');
      else if (err.code === 'functions/invalid-argument') addToast('Email inválido', 'error');
      else addToast('No se pudo enviar el email. Inténtalo más tarde.', 'error');
    }
    setResetLoading(false);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetSent(false);
  };

  // ============ Pantalla de éxito tras registro ============
  if (regSuccess) {
    return (
      <RegisterSuccess email={regSuccess} onResend={handleResendVerify} />
    );
  }

  const isLogin = mode === 'login';

  return (
    <div className={`auth-root mode-${mode}`}>
      <div className="auth-bg-orb auth-bg-orb-1" />
      <div className="auth-bg-orb auth-bg-orb-2" />
      <div className="auth-bg-orb auth-bg-orb-3" />

      <a href="/" className="back-pill">
        <i className="fa-solid fa-arrow-left"></i> Volver a torneospro.com
      </a>

      <div className="auth-card">
        <div className="auth-card-shine" />
        <div className="shatter-spread">
          <div className="shatter-left">
            <BrandPanel mode={mode} />
          </div>
          <div className="shatter-right">

            {/* Form LOGIN */}
            <div className={`shatter-form-slot ${isLogin ? 'is-active' : 'is-out'}`}>
              <form className="auth-form" onSubmit={handleLogin}>
                <div className="auth-form-head">
                  <span className="tp-eyebrow">PANEL DE ADMINISTRACIÓN</span>
                  <h1 className="auth-form-title">Inicia sesión</h1>
                  <p className="auth-form-sub">Bienvenido de vuelta. Accede a tu organización.</p>
                </div>
                <div className="fld">
                  <div className="fld-row">
                    <label className="fld-lbl">Email</label>
                  </div>
                  <input className="fld-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" autoComplete="email" />
                </div>
                <div className="fld">
                  <div className="fld-row">
                    <label className="fld-lbl">Contraseña</label>
                    <button type="button" className="link-gold" onClick={() => { setResetEmail(email); setShowResetModal(true); }}>¿La has olvidado?</button>
                  </div>
                  <input className="fld-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loginLoading}>
                  {loginLoading ? <div className="spinner" style={{width: 18, height: 18, borderWidth: 2}} /> : (
                    <><i className="fa-solid fa-arrow-right-to-bracket"></i> Iniciar sesión</>
                  )}
                </button>
                <div className="divider"><span>o continúa con</span></div>
                <button type="button" className="btn btn-google" onClick={handleGoogleLogin} disabled={loginLoading}>
                  <i className="fa-brands fa-google"></i> Google
                </button>
                <p className="auth-form-foot">
                  ¿No tienes cuenta?{' '}
                  <button type="button" className="link-gold link-strong" onClick={() => switchTo('register')}>
                    Crear cuenta gratis
                  </button>
                </p>
              </form>
            </div>

            {/* Form REGISTRO */}
            <div className={`shatter-form-slot ${!isLogin ? 'is-active' : 'is-out'}`}>
              <form className="auth-form" onSubmit={handleRegister}>
                <div className="auth-form-head">
                  <span className="tp-eyebrow">PLAN FREE · SIN TARJETA</span>
                  <h1 className="auth-form-title">Crear cuenta gratis</h1>
                  <p className="auth-form-sub">1 torneo, 8 equipos, 1 deporte. Tarda menos de 2 minutos.</p>
                </div>
                <div className="fld-grid">
                  <div className="fld">
                    <label className="fld-lbl">Nombre *</label>
                    <input className="fld-input" type="text" value={regForm.name} onChange={e => updateReg('name', e.target.value)} placeholder="Nombre y apellidos" autoComplete="name" />
                  </div>
                  <div className="fld">
                    <label className="fld-lbl">Email *</label>
                    <input className="fld-input" type="email" value={regForm.email} onChange={e => updateReg('email', e.target.value)} placeholder="tu@email.com" autoComplete="email" />
                  </div>
                </div>
                <div className="fld">
                  <label className="fld-lbl">Contraseña *</label>
                  <input className="fld-input" type="password" value={regForm.password} onChange={e => updateReg('password', e.target.value)} placeholder="Mínimo 6 caracteres" autoComplete="new-password" />
                </div>
                <div className="fld-grid">
                  <div className="fld">
                    <label className="fld-lbl">Organización *</label>
                    <input className="fld-input" type="text" value={regForm.organizationName} onChange={e => updateReg('organizationName', e.target.value)} placeholder="Nombre de tu organización" />
                  </div>
                  <div className="fld">
                    <label className="fld-lbl">Ciudad</label>
                    <input className="fld-input" type="text" value={regForm.city} onChange={e => updateReg('city', e.target.value)} placeholder="Tu ciudad o pueblo" />
                  </div>
                </div>
                <label className="check">
                  <input type="checkbox" checked={regForm.accept} onChange={e => updateReg('accept', e.target.checked)} />
                  <span>Acepto los <a className="link-gold" href="/legal" target="_blank">términos</a> y la <a className="link-gold" href="/privacy" target="_blank">política de privacidad</a>.</span>
                </label>
                <button type="submit" className="btn btn-primary" disabled={regLoading}>
                  {regLoading ? <div className="spinner" style={{width: 18, height: 18, borderWidth: 2}} /> : (
                    <><i className="fa-solid fa-rocket"></i> Crear mi cuenta</>
                  )}
                </button>
                <p className="auth-form-foot">
                  ¿Ya tienes cuenta?{' '}
                  <button type="button" className="link-gold link-strong" onClick={() => switchTo('login')}>
                    Iniciar sesión
                  </button>
                </p>
              </form>
            </div>

            {/* Slats animados (cortina veneciana dorada). Estilos inline para
                bypass de cualquier conflicto/cache de CSS externo. */}
            {animKey > 0 && (
              <div
                key={animKey}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 5,
                  pointerEvents: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {Array.from({length: 9}).map((_, i) => {
                  const dir = i % 2 ? 1 : -1;
                  return (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(90deg, transparent 0%, rgba(240,216,120,0.4) 15%, rgba(240,216,120,0.95) 40%, rgba(255,240,180,1) 50%, rgba(240,216,120,0.95) 60%, rgba(240,216,120,0.4) 85%, transparent 100%)',
                        boxShadow: '0 0 40px rgba(240,216,120,0.7), 0 0 80px rgba(240,216,120,0.4)',
                        borderBottom: '1px solid rgba(0,0,0,0.15)',
                        animation: `slat-sweep-${dir > 0 ? 'right' : 'left'} 1100ms cubic-bezier(0.55, 0, 0.2, 1) both`,
                        animationDelay: `${i * 80}ms`,
                      }}
                    />
                  );
                })}
                <style>{`
                  @keyframes slat-sweep-right {
                    0%   { transform: translateX(-130%); }
                    100% { transform: translateX(130%); }
                  }
                  @keyframes slat-sweep-left {
                    0%   { transform: translateX(130%); }
                    100% { transform: translateX(-130%); }
                  }
                `}</style>
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="auth-foot">
        <span>© 2026 TorneosPro</span>
        <span className="dot">·</span>
        <a href="/legal" className="foot-link">Aviso legal</a>
        <span className="dot">·</span>
        <a href="/privacy" className="foot-link">Privacidad</a>
      </div>

      {/* Modal: Restablecer contraseña */}
      <Modal
        isOpen={showResetModal}
        onClose={closeResetModal}
        title="Restablecer contraseña"
        footer={
          resetSent ? (
            <button className="btn btn-primary" onClick={closeResetModal}>Entendido</button>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={closeResetModal} disabled={resetLoading}>Cancelar</button>
              <button className="btn btn-primary" onClick={handlePasswordReset} disabled={resetLoading || !resetEmail}>
                {resetLoading ? <div className="spinner" style={{width: 18, height: 18, borderWidth: 2}} /> : (
                  <><i className="fa-solid fa-paper-plane"></i> Enviar email</>
                )}
              </button>
            </>
          )
        }
      >
        {resetSent ? (
          <div style={{textAlign: 'center', padding: 'var(--spacing-md) 0'}}>
            <div style={{
              width: 72, height: 72, margin: '0 auto var(--spacing-md)',
              background: 'rgba(0,255,136,0.15)', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="fa-solid fa-envelope-circle-check" style={{fontSize: '2rem', color: 'var(--color-green, #00ff88)'}} />
            </div>
            <h3 style={{marginBottom: 12}}>Email enviado</h3>
            <p style={{lineHeight: 1.6, marginBottom: 12}}>
              Hemos enviado un email a <strong>{resetEmail}</strong> con instrucciones para crear una nueva contraseña.
            </p>
            <p className="text-sm text-muted" style={{lineHeight: 1.5}}>
              Revisa tu bandeja de entrada y la carpeta de spam.<br/>
              El enlace caduca en 1 hora por seguridad.
            </p>
          </div>
        ) : (
          <div>
            <p style={{marginBottom: 'var(--spacing-md)', lineHeight: 1.6}}>
              Introduce el email asociado a tu cuenta y te enviaremos un enlace para crear una nueva contraseña.
            </p>
            <form onSubmit={handlePasswordReset}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  placeholder="tu@email.com"
                  autoComplete="email"
                  autoFocus
                  disabled={resetLoading}
                />
              </div>
            </form>
            <p className="text-sm text-muted" style={{marginTop: 'var(--spacing-md)', lineHeight: 1.5}}>
              <i className="fa-solid fa-info-circle" style={{marginRight: 6}} />
              Disponible para cualquier cuenta de TorneosPro.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ============ PANTALLA DE ÉXITO POST-REGISTRO ============
const RegisterSuccess = ({ email, onResend }) => (
  <div className="auth-root">
    <div className="auth-bg-orb auth-bg-orb-1" />
    <div className="auth-bg-orb auth-bg-orb-2" />

    <a href="/" className="back-pill">
      <i className="fa-solid fa-arrow-left" /> Volver a torneospro.com
    </a>

    <div className="auth-card" style={{maxWidth: 520, height: 'auto'}}>
      <div className="auth-card-shine" />
      <div style={{padding: '48px 40px', textAlign: 'center'}}>
        <div style={{
          width: 72, height: 72, margin: '0 auto 20px',
          background: 'rgba(240,216,120,0.15)', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid rgba(240,216,120,0.4)',
        }}>
          <i className="fa-solid fa-envelope" style={{fontSize: '1.8rem', color: '#f0d878'}} />
        </div>
        <h2 style={{
          fontFamily: 'var(--font-display, system-ui)',
          fontSize: '1.6rem',
          color: '#f0d878',
          marginBottom: 12,
        }}>
          Revisa tu email
        </h2>
        <p style={{lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', marginBottom: 8}}>
          Hemos enviado un email de verificación a:
        </p>
        <p style={{
          fontWeight: 700, color: '#f0d878',
          background: 'rgba(240,216,120,0.08)',
          padding: '10px 16px', borderRadius: 10,
          margin: '0 0 24px', wordBreak: 'break-all',
        }}>
          {email}
        </p>
        <p style={{lineHeight: 1.6, color: 'rgba(255,255,255,0.75)', fontSize: '0.92rem', marginBottom: 20}}>
          Pulsa el botón del email para activar tu cuenta. Después podrás iniciar sesión.
        </p>
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          <button onClick={onResend} className="btn" style={{
            background: 'rgba(255,255,255,0.06)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.15)',
          }}>
            <i className="fa-solid fa-paper-plane" /> Reenviar email
          </button>
          <a href="/admin" className="btn btn-primary">
            <i className="fa-solid fa-arrow-right-to-bracket" /> Ya verifiqué, iniciar sesión
          </a>
        </div>
      </div>
    </div>

    <div className="auth-foot">
      <span>© 2026 TorneosPro</span>
    </div>
  </div>
);

export default AuthScreen;
